import { memo, useState, useCallback, useMemo } from 'react';
import { useFetchCampaignsAssociationSettings, hasCampaignsReadAccess } from './hooks/useFetchCampaignsAssociationSettings';
import { useFetchAssociatedCampaigns } from './hooks/useFetchAssociatedCampaign';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Popover } from 'foundations-components/transitional/components/Popover';
import CampaignsPlatformLabelTag from './CampaignsPlatformLabelTag';
import CampaignsPlatformLabelPopover from './CampaignsPlatformLabelPopover';
import { Button } from 'foundations-components/transitional/components/Button';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import { useFetchAssociatedAssetObjectId } from './hooks/useFetchAssociatedAssetObjectId';
import { Scopes } from '../constants/Permissions';
import { userInfoSync } from 'hub-http/userInfo';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function CampaignsPlatformLabel({
  objectType,
  assetId,
  crmIdProperty = 'hs_object_id',
  HeaderComponent = ({
    children
  }) => /*#__PURE__*/_jsx(Text, {
    typography: "heading-50",
    children: children
  }),
  as = 'tag',
  inline = false,
  withEmptyState = true
}) {
  const {
    data: associationSettings,
    loading: associationSettingsLoading
  } = useFetchCampaignsAssociationSettings();
  const [open, setOpen] = useState(false);
  const handleOpenChange = useCallback(() => setOpen(!open), [open]);
  const hasCampaignsToolAccess = useMemo(() => {
    try {
      return userInfoSync().user.scopes.includes(Scopes.CAMPAIGNS);
    } catch (error) {
      // Rely on BE to return the correct value
      return true;
    }
  }, []);
  const {
    data: objectId
  } = useFetchAssociatedAssetObjectId({
    objectType,
    assetId,
    crmIdProperty,
    skip: !hasCampaignsToolAccess
  });
  const {
    data: associatedCampaigns,
    loading: associatedCampaignsLoading
  } = useFetchAssociatedCampaigns({
    objectType,
    objectId,
    associationSettings,
    isOneToMany: false,
    isFetchingAllObjectProperties: true,
    skip: !hasCampaignsToolAccess
  });
  if (associationSettingsLoading || !associationSettings || !associationSettings[objectType] || !hasCampaignsReadAccess(associationSettings, objectType) || !withEmptyState && (!associatedCampaigns || associatedCampaigns.length === 0)) {
    return null;
  }
  const associatedCampaign = associatedCampaigns === null || associatedCampaigns === void 0 ? void 0 : associatedCampaigns[0];
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: inline ? 'row' : 'column',
    display: inline ? 'inline-flex' : 'flex',
    "data-test-id": "campaigns-platform-label",
    "data-test-inline": inline,
    children: [/*#__PURE__*/_jsx(HeaderComponent, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaignSelect.formControlLabel"
      })
    }), associatedCampaignsLoading ? null : associatedCampaign ? /*#__PURE__*/_jsx(Popover, {
      open: open,
      onOpenChange: handleOpenChange,
      content: /*#__PURE__*/_jsx(CampaignsPlatformLabelPopover, {
        campaignCrmObject: associatedCampaign
      }),
      inlineSize: "360px",
      children: as === 'tag' ? /*#__PURE__*/_jsx(CampaignsPlatformLabelTag, {
        onClick: handleOpenChange,
        campaignCrmObject: associatedCampaign
      }) : /*#__PURE__*/_jsx(Button, {
        onClick: handleOpenChange,
        use: "link",
        children: getPropertyValue(associatedCampaign, 'hs_name')
      })
    }) : /*#__PURE__*/_jsx("span", {
      "data-test-id": "empty-campaigns-platform-label",
      children: "-"
    })]
  });
}
export default /*#__PURE__*/memo(CampaignsPlatformLabel);